import { Capacitor } from '@capacitor/core';
import { cleanNumber } from './smsCompose';

/**
 * The phone's address book, for picking people to text an invite to.
 *
 * Three sources, in order of what the device has:
 *   native   the Capacitor contacts plugin (iOS Contacts framework / Android ContactsContract),
 *            behind the OS permission prompt — the whole book comes back and the sheet does its
 *            own search and multi-select;
 *   picker   the web Contact Picker API (`navigator.contacts.select`, Chrome on Android), which
 *            is the OS picker itself — it hands back only what was ticked, no permission to hold;
 *   none     a laptop browser — the sheet offers a number field instead.
 * Nothing read here is ever sent to the API: numbers go straight into an `sms:` link.
 */
export function contactsSource() {
  try { if (Capacitor.isNativePlatform()) return 'native'; } catch { /* no shell */ }
  if (typeof navigator !== 'undefined' && navigator.contacts && typeof navigator.contacts.select === 'function') return 'picker';
  return 'none';
}

/** `{ id, name, phones: [{ number, label }] }` — only people with at least one usable number. */
export function normalizeContacts(rows) {
  const out = [];
  (rows || []).forEach((c, i) => {
    const phones = (c.phones || [])
      .map((p) => ({ number: cleanNumber(p?.number), label: (p?.label || p?.type || '').toString().toUpperCase() }))
      .filter((p) => p.number);
    // One entry per distinct number.
    const seen = new Set();
    const distinct = phones.filter((p) => (seen.has(p.number) ? false : seen.add(p.number)));
    // A text should reach the phone in their pocket: mobile-labelled numbers first, then the rest
    // in the book's own order (a stable sort keeps it).
    distinct.sort((a, b) => mobileRank(a.label) - mobileRank(b.label));
    if (!distinct.length) return;
    const name = (c.name?.display || [c.name?.given, c.name?.family].filter(Boolean).join(' ') || '').trim();
    out.push({ id: c.contactId || `c${i}`, name: name || distinct[0].number, phones: distinct });
  });
  return out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

const mobileRank = (label) => (/MOBILE|CELL|IPHONE|SMS/.test(label || '') ? 0 : /MAIN/.test(label || '') ? 1 : 2);

/** The web picker's rows look different (`name: ['…'], tel: ['…']`). */
export function normalizePicked(rows) {
  return normalizeContacts((rows || []).map((r, i) => ({
    contactId: `p${i}`,
    name: { display: Array.isArray(r.name) ? r.name[0] : r.name },
    phones: (Array.isArray(r.tel) ? r.tel : [r.tel]).map((t) => ({ number: t })),
  })));
}

export class ContactsDenied extends Error {
  constructor() { super('Contacts permission was not granted.'); this.name = 'ContactsDenied'; }
}

/**
 * Ask, then read. Resolves `{ people, received, cancelled }`: `people` is the normalised list,
 * `received` how many rows the source handed back BEFORE people without a usable number were
 * dropped (so the sheet can tell "you picked nobody" from "you picked people but shared no
 * numbers"), `cancelled` when the OS picker was dismissed. Throws `ContactsDenied` when the OS
 * says no, so the sheet can say where to turn it on. In the native shell the plugin is loaded
 * on first use: the People page must not pay for it on every visit.
 */
export async function loadContacts() {
  const source = contactsSource();
  if (source === 'native') {
    const { Contacts } = await import('@capacitor-community/contacts');
    let status = await Contacts.checkPermissions();
    if (status.contacts !== 'granted' && status.contacts !== 'limited') status = await Contacts.requestPermissions();
    if (status.contacts !== 'granted' && status.contacts !== 'limited') throw new ContactsDenied();
    const { contacts } = await Contacts.getContacts({ projection: { name: true, phones: true } });
    return { people: normalizeContacts(contacts), received: (contacts || []).length, cancelled: false };
  }
  if (source === 'picker') {
    try {
      const rows = await navigator.contacts.select(['name', 'tel'], { multiple: true });
      return { people: normalizePicked(rows), received: (rows || []).length, cancelled: false };
    } catch (e) {
      if (e?.name === 'AbortError') return { people: [], received: 0, cancelled: true };
      throw e;
    }
  }
  return { people: [], received: 0, cancelled: false };
}

/**
 * Search the book the way people type: any word of the name by prefix ("ok" finds Sam Okafor,
 * "sa ok" finds him too), accents ignored, or digits anywhere in a number ("0143", "250 555").
 * A query that is all punctuation and spaces matches everyone.
 */
export function filterContacts(people, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return people || [];
  const fold = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const digits = q.replace(/\D/g, '');
  const words = fold(q).split(/\s+/).filter(Boolean);
  return (people || []).filter((c) => {
    const nameWords = fold(c.name).split(/[\s\-.,'’]+/).filter(Boolean);
    const nameHit = words.length > 0 && words.every((w) => nameWords.some((n) => n.startsWith(w)));
    const numberHit = digits.length >= 2 && (c.phones || []).some((p) => String(p.number || '').replace(/\D/g, '').includes(digits));
    return nameHit || numberHit;
  });
}

/** `SO` for Sam Okafor, `S` for Sam, `#` for a bare number. */
export function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter((w) => /\p{L}/u.test(w));
  if (!parts.length) return '#';
  return parts.slice(0, 2).map((w) => w.replace(/[^\p{L}]/gu, '')[0] || '').join('').toUpperCase() || '#';
}
